import { useRef } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';

const stats = [
  { num: '4+',  label: 'Projects Built' },
  { num: '1+',  label: 'Years Coding' },
  { num: 'n8n', label: 'AI Workflows' },
];

export default function Hero() {
  const container = useRef();

  useGSAP(() => {
    const tl = gsap.timeline({ delay: 0.2 });

    tl.from('.hero-label', { y: 20, opacity: 0, duration: 0.6, ease: 'power3.out' })
      .from('.hero-name-1', { y: '110%', duration: 1, ease: 'power4.out' }, '-=0.2')
      .from('.hero-name-2', { y: '110%', duration: 1, ease: 'power4.out' }, '-=0.8')
      .from('.hero-sub',    { y: 30, opacity: 0, duration: 0.8, ease: 'power3.out' }, '-=0.4')
      .from('.hero-ctas',   { y: 20, opacity: 0, duration: 0.7, ease: 'power3.out' }, '-=0.3')
      .from('.hero-stats',  { y: 20, opacity: 0, duration: 0.7, stagger: 0.1, ease: 'power3.out' }, '-=0.4')
      .from('.hero-img-wrap', { x: 60, opacity: 0, duration: 1.2, ease: 'power3.out' }, '<');

    // Subtle floating for image
    gsap.to('.hero-img-wrap', {
      y: -12, duration: 3, repeat: -1, yoyo: true, ease: 'sine.inOut', delay: 1.5
    });
  }, { scope: container });

  return (
    <section id="home" ref={container} style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      paddingTop: '6rem',
      paddingBottom: '4rem',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Background grid lines */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 0,
        backgroundImage: 'linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)',
        backgroundSize: '80px 80px',
        opacity: 0.3,
        maskImage: 'radial-gradient(ellipse 70% 70% at 50% 50%, black 0%, transparent 100%)',
      }} />

      <div className="page-wrap" style={{ width: '100%', position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '4rem', alignItems: 'center' }} className="hero-grid">

          {/* Left — text */}
          <div>
            <div className="hero-label" style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem' }}>
              <div style={{ width: '2.5rem', height: '1px', background: 'var(--gold)' }} />
              <span className="label-gold">AI & Data Engineering — Aurora University</span>
            </div>

            <div style={{ overflow: 'hidden', marginBottom: '0.5rem' }}>
              <h1 className="display-xl hero-name-1">Pradeep</h1>
            </div>
            <div style={{ overflow: 'hidden', marginBottom: '2rem' }}>
              <h1 className="display-xl hero-name-2" style={{ WebkitTextStroke: '1px var(--gold)', color: 'transparent' }}>
                Kumar.
              </h1>
            </div>

            <p className="hero-sub" style={{ fontSize: '1.15rem', color: 'var(--muted)', maxWidth: '46ch', marginBottom: '2.5rem', lineHeight: 1.8 }}>
              Building intelligent web applications, AI automation workflows, and LLM-powered systems. From React frontends to n8n agent pipelines — I bridge design and intelligence.
            </p>

            <div className="hero-ctas" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '4rem' }}>
              <a href="#projects" className="btn btn-gold">View My Work</a>
              <a href="#contact" className="btn btn-outline">Get In Touch</a>
            </div>

            {/* Stats */}
            <hr className="hr" style={{ marginBottom: '2rem' }} />
            <div style={{ display: 'flex', gap: '3rem', flexWrap: 'wrap' }}>
              {stats.map(s => (
                <div key={s.num} className="hero-stats">
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', fontWeight: 700, color: 'var(--gold)' }}>{s.num}</div>
                  <div className="label" style={{ marginTop: '0.25rem' }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — photo */}
          <div className="hero-img-wrap" style={{ width: '300px', flexShrink: 0 }}>
            <div style={{ position: 'relative' }}>
              {/* Gold frame decoration */}
              <div style={{
                position: 'absolute', top: '-12px', right: '-12px',
                width: '100%', height: '100%',
                border: '1px solid var(--gold)',
                borderRadius: '2px',
                zIndex: 0,
              }} />
              <div style={{
                position: 'relative', zIndex: 1,
                background: 'var(--bg-2)',
                border: '1px solid var(--border)',
                overflow: 'hidden',
                borderRadius: '2px',
              }}>
                <img
                  src="https://res.cloudinary.com/dpeojc51e/image/upload/v1774454573/myprofilepicforportfoliowebsite_sb9coq.png"
                  alt="Pradeep Kumar"
                  style={{ width: '100%', display: 'block', objectFit: 'cover', aspectRatio: '4/5' }}
                />
                {/* Gold overlay tint */}
                <div style={{
                  position: 'absolute', inset: 0,
                  background: 'linear-gradient(to top, rgba(201,168,76,0.15), transparent)',
                }} />
              </div>
              {/* Location badge */}
              <div style={{
                position: 'absolute', bottom: '-1rem', left: '-1rem', zIndex: 2,
                background: 'var(--bg)', border: '1px solid var(--border)',
                padding: '0.5rem 0.85rem', borderRadius: '2px',
              }}>
                <span className="label" style={{ color: 'var(--gold)' }}>📍 Vizag, India</span>
              </div>
            </div>
          </div>

        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .hero-grid { grid-template-columns: 1fr !important; }
          .hero-img-wrap { width: 220px !important; margin: 0 auto; }
        }
      `}</style>
    </section>
  );
}
